/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import './SavingsByMeterDetailComponent.scss';
import UtilityMeterSavingsDetailTable from '../../shared/MeterSavingsDetailTable/UtilityMeterSavingsDetailTable';
import { convertDataForUtilitySavingsMeter } from '../../shared/Utils/Utils';
import {
  IAny,
  IUtilityBillElectData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  PreferredUnit,
  UtilityDetailMeterField,
  UtilityType
} from '../../shared/Utils/Constants';
import { IDataTableColumn } from '../../shared/MeterSavingsDetailTable/IMeterSavingsDetailTableProps';
import { getUtilitySavingsMeter } from '../../shared/Utils/unitHandling/EmaasSiteKpiDataScaling';
import { getMaxConvertedValueFromArray } from '../../shared/Utils/unitHandling/unitHandling';
import { ApiContext } from '../../../context/ApiContext';
import { ConversionType } from '../../shared/Utils/unitHandling/UnitModel';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';

export interface ISavingsByMeterDetailComponentProps {
  data: IUtilityBillElectData;
  UtilityBillType: string;
}

const scaleFactor: IAny = {
  therm: 0.1,
  'kilowatt hour': 0.003412,
  'kilowatt hours': 0.003412,
  kilowatthour: 0.003412,
  kilowatt_hour: 0.003412,
  kilowatt_hours: 0.003412,
  scf: 0.001028,
  'standard cubic feet': 0.001028,
  standardcubicfeet: 0.001028,
  'standard cubic foot': 0.001028,
  standardcubicfoot: 0.001028,
  standard_cubic_feet: 0.001028,
  standard_cubic_foot: 0.001028,
  'million btu': 1,
  millionbtu: 1,
  mmbtu: 1,
  'cubic meter': 0.03661,
  cubic_meter: 0.03661,
  'cubic meters': 0.03661,
  cubic_meters: 0.03661,
  'standard cubic meter': 0.0332005312,
  mega_joules: 0.000947817,
  'thousand cubic feet': 1.038,
  'centum cubic feet': 0.1037
};

export const SavingsByMeterDetailComponent = (
  props: ISavingsByMeterDetailComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { data, UtilityBillType } = props;
  const apiContext = useContext(ApiContext);
  const { userPreferedUnit } = apiContext;
  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }

  const { utilityCostCurrency, utilityMeasurementUnit, utilityMeters } = data;

  // fromUnit =
  //   userPreferedUnit === 'megajoule'
  //     ? userPreferedUnit
  //     : UtilityBillType.toLowerCase() === 'gas'
  //     ? PreferredUnit.Gas
  //     : UtilityBillType.toLowerCase() === 'electricity'
  //     ? (PreferredUnit.Electricity as any)
  //     : utilityMeasurementUnit;

  fromUnit = utilityMeasurementUnit;

  // fromUnit = userPreferedUnit ? userPreferedUnit : 'kilowatt hour';

  let isElectricOrGasUtility =
    UtilityBillType === UtilityType.Electricity ||
    UtilityBillType.toLowerCase() === UtilityType.Electric ||
    UtilityBillType === UtilityType.Gas;

  const valuesArray = getUtilitySavingsMeter(utilityMeters);

  // let isElectricOrGasUtility =
  //   UtilityBillType === UtilityType.Electricity ||
  //   UtilityBillType.toLowerCase() === UtilityType.Electric;
  // const valuesArray = getUtilitySavingsMeter(utilityMeters);

  // if( (UtilityBillType === UtilityType.Electricity || UtilityBillType.toLowerCase() === UtilityType.Electric) && utilityMeasurementUnit === 'watt hour') {

  // }

  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    UtilityBillType,
    utilityMeasurementUnit,
    valuesArray,
    utilityMeasurementUnit
  );

  const meterSavingdata = convertDataForUtilitySavingsMeter(
    utilityMeters,
    utilityMeasurementUnit,
    maxValueUnitConversion.Unit,
    UtilityBillType,
    apiContext.showChangeWhenData
  );

  const columnDetails: Array<IDataTableColumn> = [
    {
      className: 'datatable-column-meter',
      field: UtilityDetailMeterField.Meter,
      header: t(d.METER),
      sortable: true,
      initialWidth: '10%'
    },
    {
      className: 'datatable-column-actualConsumption',
      field: UtilityDetailMeterField.totalActualConsumption,
      header: t(d.actualCons),
      sortable: true,
      initialWidth: '16%'
    },
    {
      className: 'datatable-column-baselineConsumption',
      field: UtilityDetailMeterField.previousPeriodConsumption,
      header: t(d.PREVIOUSPERIOD),
      sortable: true,
      initialWidth: '10%'
    },
    {
      className: 'datatable-column-percentageConsumptionSaving',
      field: UtilityDetailMeterField.totalConsumptionSavings,
      header: t(d.ConsumptionChange),
      sortable: true,
      initialWidth: '9%'
    },
    {
      className: 'datatable-column-actualCost',
      field: UtilityDetailMeterField.totalActualCost,
      header: t(d.utiltactualCost),
      sortable: true,
      initialWidth: '15%'
    },
    {
      className: 'datatable-column-utilityBaselineCost',
      field: UtilityDetailMeterField.previousPeriodCost,
      header: t(d.utilpreviousCost),
      sortable: true,
      initialWidth: '9%'
    },
    {
      className: 'datatable-column-percentageCostSavings',
      field: UtilityDetailMeterField.totalCostSavings,
      header: t(d.utilchange),
      sortable: true,
      initialWidth: '8.5%'
    }
  ];

  return (
    <div className="emaas-Metersaving-details-head Electricity-Basic-Tab-Table">
      <UtilityMeterSavingsDetailTable
        meterData={meterSavingdata}
        utilityCostCurrency={utilityCostCurrency!}
        utilityMeasurementUnit={maxValueUnitConversion.Unit}
        columnDetails={columnDetails}
        utilityType={UtilityBillType}
        originalUnit={utilityMeasurementUnit}
      />
    </div>
  );
};
export default SavingsByMeterDetailComponent;
